import type { CurrencyTotal } from "@/composables/formatUtils";
import {
  Company,
  IndicativePayoutRate,
  Invoice,
  InvoiceItem,
  PendingPayout,
  PendingPayoutByCompanyId,
} from "@/types";
import {
  Currency,
  DEFAULT_CURRENCY,
  SUPPORTED_CURRENCIES,
} from "@/money/currency_constants";
import {
  entries,
  filter,
  find,
  flatMap,
  groupBy,
  keyBy,
  map,
  mapValues,
  sortBy,
  sumBy,
} from "lodash-es";
import { fromUnits } from "./moneyUtils";

export type Payout = {
  company_id: number;
  items: InvoiceItem[];
};

/** The exact ledger values an invoice line carries, each in the line's own currency. */
type LedgerUnits = "gross_units" | "net_units" | "vat_units";

/** Pending lines grouped per company. */
export const getPendingPayouts = (invoiceItems: InvoiceItem[]) => {
  if (!invoiceItems?.length) {
    return [];
  }

  const invoicesByCompanyId = groupBy(invoiceItems, "company_id");

  const payouts = map(
    entries(invoicesByCompanyId),
    ([_company_id, items]): Payout => {
      const company_id = parseInt(_company_id);

      if (isNaN(company_id)) {
        throw new Error(`Invalid company_id: ${_company_id}`);
      }

      return { company_id, items };
    }
  );

  return payouts;
};

/** Sums display_amount per currency: invoices issued in different currencies are never added together. */
export const sumDisplayAmountByCurrency = (
  invoices: Invoice[]
): Record<string, number> =>
  mapValues(groupBy(invoices, "currency"), (group) =>
    sumBy(group, "display_amount")
  );

/**
 * Invoice lines' values summed per currency, in SUPPORTED_CURRENCIES order: lines in different
 * currencies are never added together. Each currency's exact ledger units are summed, then
 * converted once, so sub-cent lines round once. A currency no line is in is left out.
 *
 * @param units - Which of the line's values to total; the gross value by default
 */
export const totalsByCurrency = <Units extends LedgerUnits = "gross_units">(
  items: ({ currency: string } & Record<NoInfer<Units>, number>)[],
  units: Units = "gross_units" as Units
): CurrencyTotal[] =>
  sortBy(
    map(groupBy(items, "currency"), (group, currency) => ({
      // The one bridge from the API's string currency: the backend only issues supported ones.
      currency: currency as Currency,
      amount: fromUnits(sumBy(group, units)),
    })),
    (total) => currencyOrder(total.currency)
  );

/**
 * What is owed per currency on pending lines: a sold line carries a negative value.
 *
 * @param units - Which of the line's values to total; the gross value by default
 */
export const pendingPayoutTotals = (
  items: InvoiceItem[],
  units: LedgerUnits = "gross_units"
): CurrencyTotal[] =>
  map(totalsByCurrency(items, units), (total) => ({
    ...total,
    // Subtracted from zero rather than negated, so a currency that nets to nothing owes 0, not -0.
    amount: 0 - total.amount,
  }));

/** A sort key putting currencies in SUPPORTED_CURRENCIES order; one the frontend does not know sorts last, never before EUR. */
export const currencyOrder = (currency: Currency): number => {
  const index = SUPPORTED_CURRENCIES.indexOf(currency);
  return index === -1 ? SUPPORTED_CURRENCIES.length : index;
};

// Wider than any display_amount, so the currency always dominates the amount in the sort key.
const CURRENCY_SORT_SPAN = 1e12;

/** A numeric sort key ordering invoices by currency (in SUPPORTED_CURRENCIES order), then by amount within it. */
export const currencyGroupedAmount = (
  invoice: Pick<Invoice, "currency" | "display_amount">
): number =>
  SUPPORTED_CURRENCIES.indexOf(invoice.currency as Currency) *
    CURRENCY_SORT_SPAN +
  invoice.display_amount;

/**
 * Part of a pending payout converts at today's ECB rate, so the amount is indicative: a payout
 * requested later converts that part at its own date's rate. `rate` is today's rate into the payout
 * currency, while the account tree carries one.
 */
export interface PendingPayoutConversion {
  rate: IndicativePayoutRate | null;
}

/** What is owed in one currency, for a "Money pending" figure. */
export interface PendingPayoutPart {
  currency: Currency;
  amount: number;
  conversion: PendingPayoutConversion | null;
}

/**
 * What is owed per currency, in SUPPORTED_CURRENCIES order, never adding currencies together: the
 * single rule for whether money is pending. A company counts as the backend's pending payout for it
 * (`PendingPayout`), the figure its payout would pay; where the backend has none — no recent ECB rate
 * — its items count unconverted, in the currencies they are owed in. Companies net within a
 * currency; a currency nothing is owed in is left out, so no pending payout gives an empty list.
 */
export const pendingPayoutsByPayoutCurrency = (
  invoiceItems: InvoiceItem[],
  companies: Company[],
  pendingPayoutByCompanyId: PendingPayoutByCompanyId
): PendingPayoutPart[] => {
  const companiesById = keyBy(companies, "id");
  const companyParts = flatMap(getPendingPayouts(invoiceItems), (payout) =>
    // Until its refetch lands a dashboard may still hold the previous company's items: their company
    // is missing from `companies`, so no rate is named, and items without a figure count unconverted.
    companyPendingPayoutParts(
      payout.items,
      pendingPayoutByCompanyId[payout.company_id],
      companiesById[payout.company_id]
    )
  );
  const currencyParts = map(
    groupBy(companyParts, "currency"),
    (group): PendingPayoutPart => ({
      currency: group[0].currency,
      amount: sumBy(group, "amount"),
      // Companies paid out in one currency share today's rate for it.
      conversion:
        find(group, (part) => part.conversion !== null)?.conversion ?? null,
    })
  );
  return sortBy(
    filter(currencyParts, (part) => part.amount > 0),
    (part) => currencyOrder(part.currency)
  );
};

/**
 * What one company's "Money pending" figure shows: what is owed while anything is, else its
 * balance per currency, unconverted — an EUR company's fee-only balance reads "-17.22 €". No items
 * read zero euros.
 */
export const pendingPayoutFigureParts = (
  invoiceItems: InvoiceItem[],
  company: Company,
  pendingPayoutByCompanyId: PendingPayoutByCompanyId
): PendingPayoutPart[] => {
  const owed = pendingPayoutsByPayoutCurrency(
    invoiceItems,
    [company],
    pendingPayoutByCompanyId
  );
  if (owed.length > 0) {
    return owed;
  }
  const balance = map(
    pendingPayoutTotals(invoiceItems),
    (total): PendingPayoutPart => ({ ...total, conversion: null })
  );
  return balance.length > 0
    ? balance
    : [{ currency: DEFAULT_CURRENCY, amount: 0, conversion: null }];
};

// One company's pending items as the parts its figure shows: the backend's pending payout while it
// has one, else the items' totals per currency, unconverted. An amount of nothing adds nothing, and
// must not mark its currency's sum as converted.
const companyPendingPayoutParts = (
  items: InvoiceItem[],
  pendingPayout: PendingPayout | null | undefined,
  company: Company | undefined
): PendingPayoutPart[] => {
  const parts: PendingPayoutPart[] = pendingPayout
    ? [
        {
          currency: pendingPayout.currency,
          amount: Number(pendingPayout.amount),
          conversion: pendingPayout.converted
            ? { rate: company?.indicative_payout_rate ?? null }
            : null,
        },
      ]
    : map(pendingPayoutTotals(items), (total): PendingPayoutPart => ({
        ...total,
        conversion: null,
      }));
  return filter(parts, (part) => part.amount !== 0);
};
